import { ref, toValue, watch, type MaybeRef, type Ref } from 'vue'

import { apiFetch } from '@ja/portal-query'

import { useInboxChannel, type UnreadCountChangedEvent } from './useInboxChannel'

export interface UseUnreadCountReturn {
  totalUnread: Ref<number>
  byType: Ref<Record<string, number>>
  isConnected: Ref<boolean>
  refresh: () => Promise<void>
}

/**
 * Reactive unread count with real-time updates.
 *
 * Provides a reactive unread count that updates in real-time via WebSocket
 * and can be manually refreshed via API.
 */
export function useUnreadCount(userId: MaybeRef<string | null | undefined>): UseUnreadCountReturn {
  const totalUnread = ref(0)
  const byType = ref<Record<string, number>>({})

  const handleUnreadCountChanged = (event: UnreadCountChangedEvent) => {
    totalUnread.value = event.totalUnread
    byType.value = event.byType
  }

  const { isConnected } = useInboxChannel(userId, {
    onUnreadCountChanged: handleUnreadCountChanged,
  })

  const refresh = async (): Promise<void> => {
    const id = toValue(userId)
    if (!id) return

    try {
      const data = await apiFetch<any>('/api/communications/unread/count')
      totalUnread.value = data.total
      byType.value = data.by_type
    } catch {
      // Silently fail - will retry on next refresh
    }
  }

  // Initial fetch when userId is available
  watch(
    () => toValue(userId),
    (newId) => {
      if (newId) {
        void refresh()
      } else {
        totalUnread.value = 0
        byType.value = {}
      }
    },
    { immediate: true },
  )

  return {
    totalUnread,
    byType,
    isConnected,
    refresh,
  }
}
